import { useState } from 'react';

import { useBookingPaymentAccounts } from '@/api/bookings';
import type { Customer } from '@/api/masters';
import { DesignPhoto } from '@/components/DesignPhoto';
import { textAreaClass } from '@/components/Field';
import { Button, Card, Notice } from '@/components/PageHeading';
import { QuickAmounts, SplitBoxes } from '@/components/SplitBoxes';
import { bagOf } from '@core/bookings/bag';
import { formatPct } from '@core/bookings/discount';
import { discountReasons } from '@core/bookings/discountForm';
import {
  depositFigure,
  depositMode,
  depositPatch,
  depositPctChoices,
  discountFigure,
  discountMode,
  discountPatch,
  discountPctChoices,
  pctOf,
  tokenFigure,
  tokenMode,
  tokenPatch,
  tokenPctChoices,
} from '@core/bookings/moneyChoice';
import {
  choosePayer,
  draftMoney,
  removeWearer,
  type BookingDraft,
  type DraftProblem,
} from '@core/bookings/draft';
import { chargeableDays } from '@core/bookings/pricing';
import {
  bagBoxes,
  bagPartsProblem,
  bagTakenNow,
  confirmLabel,
  depositAtBooking,
  paymentsAtBooking,
} from '@core/bookings/takeBooking';
import { fillOne, type SplitPart } from '@core/cash/split';
import { takenHeadline, type TakenGarment } from '@core/bookings/takenMeanwhile';
import { formatAmount } from '@core/format/money';
import { withReference } from '@core/units/referenceNumber';

import { CustomerPicker } from '../../customers/CustomerPicker';
import { DepositDialog } from './MoneyDialogs';
import { PercentOrAmount } from './PercentOrAmount';

/**
 * Step 3 of a new booking on the website, as on the phone: **the bag, the customer, and the money
 * already filled in.** The customer is picked here, at checkout, by mobile number or name — the one
 * thing on the screen with no default.
 *
 * <p>
 * <strong>The money</strong>: rental, less the discount, less the token, leaves what is paid at
 * pickup — and the deposit in its own box, because it is held and comes back rather than being rent,
 * and is not a supply. Discount, token and deposit are each a % / ₹ choice with quick buttons and a
 * box to type any figure (canvas "Booking flow", 2026-10-26; `moneyChoice.ts` maps them onto the
 * draft's own fields). When the deposit is taken and given back stays behind its link.
 * </p>
 */
export function BagStep({
  draft,
  branchId,
  problems,
  taken,
  currency,
  shopMultiplier,
  busy,
  onChange,
  onAddMore,
  onOpenGarment,
  onReplaceTaken,
  onCreateCustomer,
  onConfirm,
  onInstead,
}: {
  draft: BookingDraft;
  branchId: number | undefined;
  problems: readonly DraftProblem[];
  /** Garments another counter booked meanwhile (`takenMeanwhile`). */
  taken: readonly TakenGarment[];
  currency: string;
  shopMultiplier: number;
  busy: boolean;
  onChange: (next: BookingDraft) => void;
  onAddMore: () => void;
  onOpenGarment: (unitId: number) => void;
  onReplaceTaken: (only?: TakenGarment) => void;
  /** A search that found nobody — the create form opens with what was typed. */
  onCreateCustomer: (typed: string) => void;
  onConfirm: () => void;
  onInstead: () => void;
}) {
  const cash = (amount: number) => `${currency}${formatAmount(amount)}`;
  const bag = bagOf(draft);
  const money = draftMoney(draft);
  const days = chargeableDays(draft.chargeFrom, draft.chargeTo);
  const [panel, setPanel] = useState<'deposit' | null>(null);
  const [picking, setPicking] = useState(draft.customerId === null);
  const patch = (changes: Partial<BookingDraft>) => onChange({ ...draft, ...changes });

  const confirmed: BookingDraft = { ...draft, startAs: 'Confirmed' };
  const paysNow = paymentsAtBooking(confirmed).length > 0;
  const lost = new Set(taken.map((garment) => garment.unitId));
  const replaceable = taken.filter((garment) => garment.replacement !== null);
  // What is taken now, a box per account, on its own card after the deposit (split payments; owner,
  // 2026-11-01). Untouched, all of it sits in the till's box — what is sent while nothing is typed.
  const accounts = useBookingPaymentAccounts(branchId);
  const takenNow = bagTakenNow(confirmed);
  const boxes = bagBoxes(confirmed, accounts.data);
  const changeBoxes = (next: (current: SplitPart[]) => SplitPart[]) =>
    patch({ payParts: next(boxes) });
  const splitIssue = paysNow ? bagPartsProblem(confirmed, currency) : null;
  const blocked = problems.length > 0 || taken.length > 0 || busy || splitIssue !== null;

  const depositWhen =
    draft.depositCollectionPoint === 'AtBooking'
      ? 'taken now, with the token'
      : draft.depositCollectionPoint === 'Split'
        ? 'half now, half at pickup'
        : 'taken at pickup';

  const pick = (customer: Customer | null) => {
    onChange(choosePayer(draft, customer ? { id: customer.id, name: customer.name } : null));
    setPicking(customer === null);
  };

  return (
    <div className="mx-auto grid w-full max-w-[1200px] grid-cols-12 gap-4">
      {/* ------------------------------------------------- who, and what they wear */}
      <div className="col-span-12 flex flex-col gap-4 lg:col-span-7">
        <Card>
          <p className="mb-3 text-[15px] font-bold text-gray-900">Who is paying</p>
          {picking || draft.customerId === null ? (
            <>
              <CustomerPicker
                branchId={branchId}
                value={null}
                autoFocus
                disabled={busy}
                onSelect={pick}
                onCreateRequested={onCreateCustomer}
              />
              <p className="text-default mt-2 text-[12px]">
                Find them by mobile or name. A number nobody has yet adds the customer here.
              </p>
            </>
          ) : (
            <div className="flex items-center gap-3">
              <span className="bg-primary-transparent text-primary flex size-10 shrink-0 items-center justify-center rounded-full text-[13px] font-bold">
                {initials(draft.customerName)}
              </span>
              <p className="min-w-0 flex-1 truncate text-[15px] font-semibold text-gray-900">
                {draft.customerName}
              </p>
              <ChangeLink label="Change" onClick={() => setPicking(true)} />
            </div>
          )}
        </Card>

        {taken.length > 0 && (
          <Notice tone="warning" title={takenHeadline(taken)}>
            <ul className="mb-2 flex list-none flex-col gap-1.5 ps-0">
              {taken.map((garment) => (
                <li
                  key={garment.unitId}
                  className="flex list-none flex-wrap items-center justify-between gap-2"
                >
                  <span className="min-w-0">
                    <span className="font-mono-nums text-gray-900">
                      {withReference(garment.serialCode, garment.referenceNumber)}
                    </span>{' '}
                    for {garment.wearerName} · {garment.back}
                    {garment.replacement ? (
                      <>
                        {' '}
                        —{' '}
                        <span className="font-mono-nums text-gray-900">
                          {withReference(
                            garment.replacement.serialCode,
                            garment.replacement.referenceNumber,
                          )}
                        </span>{' '}
                        is free
                      </>
                    ) : (
                      ' — none of that size is free; open it to pick another design'
                    )}
                  </span>
                  {garment.replacement ? (
                    <Button disabled={busy} onClick={() => onReplaceTaken(garment)}>
                      Swap
                    </Button>
                  ) : (
                    <Button disabled={busy} onClick={() => onOpenGarment(garment.unitId)}>
                      Open
                    </Button>
                  )}
                </li>
              ))}
            </ul>
            {replaceable.length > 1 && (
              <Button variant="primary" disabled={busy} onClick={() => onReplaceTaken()}>
                Swap all {replaceable.length}
              </Button>
            )}
          </Notice>
        )}

        {bag.people.map((person) => (
          <Card key={person.key}>
            <div className="mb-1 flex items-center justify-between gap-2">
              <p className="text-default min-w-0 truncate text-[11.5px] font-semibold tracking-wide uppercase">
                {person.name}
                {person.relation && ` · ${person.relation}`}
              </p>
              <span className="font-mono-nums text-[13px] font-semibold text-gray-900">
                {cash(person.subtotal)}
              </span>
            </div>

            {person.lines.length === 0 ? (
              <div className="flex items-center justify-between gap-2 py-2">
                <p className="text-default text-[12.5px]">Nothing for {person.name} yet.</p>
                <ChangeLink
                  label={`Remove ${person.name}`}
                  onClick={() => onChange(removeWearer(draft, person.key))}
                />
              </div>
            ) : (
              <ul className="flex list-none flex-col ps-0">
                {person.lines.map((line) => (
                  <li
                    key={line.unitId}
                    className="border-border-color flex list-none items-center gap-3 border-t py-2 first:border-t-0"
                  >
                    <div className="w-11 shrink-0 overflow-hidden rounded">
                      <DesignPhoto
                        url={line.imageUrl}
                        alt={line.name}
                        categoryPath={line.categoryName}
                      />
                    </div>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-semibold text-gray-900">
                        {line.name}
                      </span>
                      <span className="text-default font-mono-nums block truncate text-[11.5px]">
                        {withReference(line.serialCode, line.referenceNumber)}
                        {line.discounted && <span className="text-primary"> · discounted</span>}
                        {lost.has(line.unitId) && (
                          <span className="text-warning font-semibold"> · taken meanwhile</span>
                        )}
                      </span>
                    </span>
                    <span className="font-mono-nums text-[13px] font-semibold text-gray-900">
                      {line.charged === null ? '—' : cash(line.charged)}
                    </span>
                    <ChangeLink
                      label="Change"
                      aria={`Change ${line.name}, ${line.serialCode}`}
                      onClick={() => onOpenGarment(line.unitId)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ))}

        <div>
          <Button icon="icon icon-plus" disabled={busy} onClick={onAddMore}>
            Add a garment
          </Button>
        </div>

        <Card>
          <label className="block">
            <span className="mb-1 block text-sm font-semibold text-gray-900">
              Note for the counter
            </span>
            <textarea
              rows={2}
              value={draft.notes}
              disabled={busy}
              placeholder="Fitting on Saturday, sleeves to check"
              onChange={(event) => patch({ notes: event.target.value })}
              className={textAreaClass}
            />
          </label>
        </Card>
      </div>

      {/* --------------------------------------------------------------- the money */}
      <div className="col-span-12 flex flex-col gap-4 lg:col-span-5">
        <Card>
          <p className="mb-3 text-[15px] font-bold text-gray-900">The money</p>
          <div className="flex flex-col gap-3">
            <Line
              label={`Rent · ${plural(bag.garments, 'garment')}, ${plural(days, 'day')}`}
              value={cash(money.rental)}
            />

            {(draft.discountCeilingPct > 0 || money.discount > 0) && (
              <PercentOrAmount
                title="Discount"
                mode={discountMode(draft)}
                figure={discountFigure(draft)}
                money={money.discount}
                base={money.rental}
                pctChoices={discountPctChoices(draft.discountCeilingPct)}
                amountChoices={discountPctChoices(draft.discountCeilingPct).map((pct) =>
                  Math.round((money.rental * pct) / 100),
                )}
                zeroLabel="No discount"
                currency={currency}
                disabled={busy}
                onChange={(mode, value) => patch(discountPatch(mode, value))}
              >
                <p className="text-default mb-0 text-[12px]">
                  Your limit is {formatPct(draft.discountCeilingPct)}% of the rent. Off one garment
                  only: use Change on that garment.
                </p>
                {money.discount > 0 && (
                  <div>
                    <p className="mb-1.5 text-[13px] font-semibold text-gray-900">
                      Why <span className="text-danger">*</span>
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {discountReasons.map((reason) => (
                        <button
                          key={reason}
                          type="button"
                          disabled={busy}
                          aria-pressed={draft.discountReason === reason}
                          onClick={() => patch({ discountReason: reason })}
                          className={`h-8 cursor-pointer rounded-full border px-3 text-[13px] ${
                            draft.discountReason === reason
                              ? 'border-primary bg-primary-transparent text-primary font-semibold'
                              : 'border-border-color bg-white text-gray-900 hover:bg-light'
                          }`}
                        >
                          {reason}
                        </button>
                      ))}
                      <input
                        aria-label="Another reason"
                        placeholder="Another reason"
                        value={
                          discountReasons.includes(draft.discountReason) ? '' : draft.discountReason
                        }
                        disabled={busy}
                        onChange={(event) => patch({ discountReason: event.target.value })}
                        className="border-border-color h-8 min-w-40 flex-1 rounded-full border bg-white px-3 text-[13px] text-gray-900"
                      />
                    </div>
                  </div>
                )}
                {money.discountOverCeiling && (
                  <Notice
                    tone="warning"
                    title={`That is ${formatPct(money.discountPct)}% off — over your limit of ${formatPct(draft.discountCeilingPct)}%.`}
                  >
                    Ask the owner, or give less.
                  </Notice>
                )}
                <Line
                  label={
                    money.discount > 0
                      ? `${formatPct(money.discountPct)}% off${draft.discountReason ? ` · ${draft.discountReason.toLowerCase()}` : ''}`
                      : 'No discount'
                  }
                  value={money.discount > 0 ? `− ${cash(money.discount)}` : '—'}
                  tone="good"
                />
              </PercentOrAmount>
            )}

            <Line label="Rent after discount" value={cash(money.netRental)} strong />

            <PercentOrAmount
              title="Token today"
              mode={tokenMode(draft)}
              figure={tokenFigure(draft)}
              money={money.token}
              base={money.netRental}
              pctChoices={tokenPctChoices}
              amountChoices={[0, 1000, 2000, 5000].filter((amount) => amount <= money.netRental)}
              zeroLabel="No token"
              pctLabel={(pct) => (pct === 100 ? 'All of it' : `${pct}%`)}
              currency={currency}
              disabled={busy}
              tinted
              onChange={(mode, value) => patch(tokenPatch(mode, value))}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-default text-[13px]">
                  {money.token > 0
                    ? `${formatPct(pctOf(money.token, money.netRental))}% of the rent after discount`
                    : 'Nothing taken today'}
                </span>
                <span className="text-primary font-mono-nums font-serif text-[22px] font-semibold">
                  {money.token > 0 ? cash(money.token) : '—'}
                </span>
              </div>
            </PercentOrAmount>

            <Line label="Rent left at pickup" value={cash(money.balance)} strong />

            {/* Held, and comes back — so its own box, never among the rent. */}
            <PercentOrAmount
              title="Security deposit"
              aside={depositWhen}
              mode={depositMode(draft)}
              figure={depositFigure(draft)}
              money={money.deposit}
              base={money.rental}
              pctChoices={depositPctChoices(shopMultiplier)}
              amountChoices={[5000, 10000, 15000, 20000]}
              currency={currency}
              disabled={busy}
              onChange={(mode, value) => patch(depositPatch(mode, value))}
            >
              <Line
                label={`${formatPct(pctOf(money.deposit, money.rental))}% of the rent, before discount`}
                value={cash(money.deposit)}
              />
              {money.depositOverridden && (
                <input
                  aria-label="Why the deposit was changed"
                  placeholder="Why it was changed (kept with the booking)"
                  value={draft.depositOverrideReason}
                  disabled={busy}
                  onChange={(event) => patch({ depositOverrideReason: event.target.value })}
                  className="border-border-color h-9 rounded-lg border bg-white px-3 text-[13px] text-gray-900"
                />
              )}
              <p className="text-default mb-0 text-[12px]">
                Usual for this shop: {formatPct(shopMultiplier * 100)}% of the rent. Refundable, and
                not part of the invoice — a deposit is not a supply.{' '}
                <ChangeLink
                  label="When it is taken and given back"
                  onClick={() => setPanel('deposit')}
                />
              </p>
            </PercentOrAmount>

            <p className="text-default mb-0 text-[11.5px]">
              GST is added on the invoice when the garments are handed over.
            </p>
          </div>
        </Card>

        {paysNow && (
          <Card>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[13px] font-semibold text-gray-900">
                {depositAtBooking(confirmed) > 0
                  ? 'Taken now · token and deposit'
                  : 'Taken now · token'}
              </span>
              <span className="text-primary font-mono-nums font-serif text-[22px] font-semibold">
                {cash(takenNow)}
              </span>
            </div>
            <p className="text-default mt-1 mb-2.5 text-[12px]">
              Into which accounts — split it across more than one if the customer pays two ways.
            </p>
            {boxes.length === 0 ? (
              <p className="text-default mb-0 text-[12px]">
                {accounts.isPending
                  ? 'Loading the accounts…'
                  : 'Taken in cash — the till is opened on its first payment.'}
              </p>
            ) : (
              <div className="flex flex-col gap-2.5">
                <SplitBoxes parts={boxes} onChange={changeBoxes} direction="into" busy={busy} />
                <QuickAmounts
                  options={[{ label: `All ${cash(takenNow)}`, amount: takenNow }]}
                  busy={busy}
                  onPick={(amount) => patch({ payParts: fillOne(boxes, amount) })}
                />
              </div>
            )}
          </Card>
        )}

        {problems.map((problem) => (
          <Notice key={problem.message} tone="warning" title={problem.message} />
        ))}
        {splitIssue && <Notice tone="warning" title={splitIssue} />}

        <Card>
          <Button
            variant="primary"
            data-enter-key-commit
            shortcut="save"
            disabled={blocked}
            onClick={onConfirm}
            className="h-12 w-full justify-center text-[15px]"
          >
            {confirmLabel(confirmed, currency)}
          </Button>
          <button
            type="button"
            disabled={blocked}
            onClick={onInstead}
            className="text-primary mt-2 w-full cursor-pointer py-1.5 text-[13px] font-semibold disabled:cursor-not-allowed disabled:opacity-50"
          >
            Hold it, or give a quotation instead
          </button>
          <p className="text-default mb-0 text-center text-[12px]">
            A hold keeps the garments without a token; a quotation keeps nothing.
          </p>
        </Card>
      </div>

      {panel === 'deposit' && (
        <DepositDialog
          draft={draft}
          shopMultiplier={shopMultiplier}
          currency={currency}
          patch={patch}
          onClose={() => setPanel(null)}
        />
      )}
    </div>
  );
}

/** One figure of the money: what it is on the left, the amount on the right. */
function Line({
  label,
  value,
  strong = false,
  tone,
}: {
  label: string;
  value: string;
  strong?: boolean;
  tone?: 'good';
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 px-0.5">
      <span className={`text-[13px] ${strong ? 'font-semibold text-gray-900' : 'text-default'}`}>
        {label}
      </span>
      <span
        className={`font-mono-nums ${strong ? 'text-[15px] font-bold' : 'text-[13px] font-semibold'} ${
          tone === 'good' ? 'text-[color:var(--color-success)]' : 'text-gray-900'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

function ChangeLink({
  label,
  aria,
  onClick,
}: {
  label: string;
  aria?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={aria}
      onClick={onClick}
      className="text-primary shrink-0 cursor-pointer text-[12.5px] font-semibold hover:underline"
    >
      {label}
    </button>
  );
}

function initials(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}
